import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppState } from './AppLayout'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useStopArrivals } from '@/lib/queries'
import { greekToLatin } from '@/lib/api'
import { planDirectTrips, searchPlaces, walkMinutes, type Place, type TripOption } from '@/lib/tripPlanner'

type Lang = 'el' | 'en'

const L = {
  title: { el: 'Σχεδιασμός διαδρομής', en: 'Plan a trip' },
  from: { el: 'Από', en: 'From' },
  to: { el: 'Προς', en: 'To' },
  myLocation: { el: 'Η τοποθεσία μου', en: 'My location' },
  locating: { el: 'Εντοπισμός τοποθεσίας...', en: 'Finding your location...' },
  enableLocation: { el: 'Ενεργοποίηση τοποθεσίας', en: 'Enable location' },
  placeholder: { el: 'Αναζήτηση προορισμού (π.χ. Lidl Αλίμου)', en: 'Search destination (e.g. Lidl Alimou)' },
  search: { el: 'Αναζήτηση', en: 'Search' },
  searching: { el: 'Αναζήτηση...', en: 'Searching...' },
  noPlaces: { el: 'Δεν βρέθηκαν μέρη.', en: 'No places found.' },
  options: { el: 'Απευθείας λεωφορεία', en: 'Direct buses' },
  noOptions: { el: 'Δεν βρέθηκε απευθείας λεωφορείο (έως ~900μ περπάτημα).', en: 'No direct bus found (within ~900m walk at each end).' },
  walkTo: { el: 'Περπατήστε', en: 'Walk' },
  board: { el: 'Επιβιβαστείτε στη στάση', en: 'Board at' },
  alight: { el: 'Αποβιβαστείτε στη στάση', en: 'Get off at' },
  stops: { el: 'στάσεις', en: 'stops' },
  walkToDest: { el: 'και περπατήστε', en: 'then walk' },
  toDest: { el: 'έως τον προορισμό', en: 'to destination' },
  nextBus: { el: 'Επόμενο λεωφορείο', en: 'Next bus' },
  noLive: { el: 'Χωρίς ζωντανή πρόβλεψη τώρα', en: 'No live arrival right now' },
  liveEta: { el: 'Ζωντανή άφιξη →', en: 'Live arrival →' },
  min: { el: 'λεπτά', en: 'min' },
}
const tr = (key: keyof typeof L, lang: Lang) => L[key][lang]

function TripOptionCard({ option, lang }: { option: TripOption; lang: Lang }) {
  const { data: arrivals, isLoading } = useStopArrivals(option.board.stopCode)
  const minutes = (Array.isArray(arrivals) ? arrivals : [])
    .filter(a => a.route_code === option.routeCode)
    .map(a => parseInt(a.btime2))
    .filter(n => Number.isFinite(n))
    .sort((a, b) => a - b)
  const lineSlug = greekToLatin(option.lineId).toLowerCase()
  const stopName = (s: TripOption['board']) => (lang === 'en' ? s.nameEng || s.name : s.name)
  const routeName = lang === 'en' ? option.routeDescrEng || option.routeDescr : option.routeDescr

  return (
    <div className="plan-option">
      <div className="plan-option-head">
        <span className="plan-line">{option.lineId}</span>
        <span className="plan-route-name">{routeName}</span>
      </div>
      <ol className="plan-steps">
        <li>🚶 {tr('walkTo', lang)} {option.walkToBoardM}m (~{walkMinutes(option.walkToBoardM)} {tr('min', lang)})</li>
        <li>🚏 {tr('board', lang)} <strong>{stopName(option.board)}</strong> <span className="plan-muted">#{option.board.stopCode}</span></li>
        <li>🚌 {option.stopsCount} {tr('stops', lang)} → {tr('alight', lang)} <strong>{stopName(option.alight)}</strong> <span className="plan-muted">#{option.alight.stopCode}</span></li>
        <li>🚶 {tr('walkToDest', lang)} {option.walkFromAlightM}m (~{walkMinutes(option.walkFromAlightM)} {tr('min', lang)}) {tr('toDest', lang)}</li>
      </ol>
      <div className="plan-live">
        <span>
          {tr('nextBus', lang)}:{' '}
          {isLoading ? '…' : minutes.length > 0
            ? <strong>{minutes.slice(0, 3).join(', ')} {tr('min', lang)}</strong>
            : <span className="plan-muted">{tr('noLive', lang)}</span>}
        </span>
        <Link className="plan-live-link" to={`/${lineSlug}/${option.routeCode}/${option.board.stopCode}`}>
          {tr('liveEta', lang)}
        </Link>
      </div>
    </div>
  )
}

export function PlanTripPage() {
  const { lang } = useAppState()
  const { location, error: locationError, isLoading: locating, requestLocation } = useGeolocation(true)
  const [query, setQuery] = useState('')
  const [places, setPlaces] = useState<Place[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [destination, setDestination] = useState<Place | null>(null)

  const runSearch = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    setSearchError(null)
    setDestination(null)
    try {
      setPlaces(await searchPlaces(query.trim()))
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : String(err))
      setPlaces(null)
    } finally {
      setSearching(false)
    }
  }

  const options = useMemo(
    () => (location && destination ? planDirectTrips(location, destination) : []),
    [location, destination],
  )

  return (
    <div className="plan-page">
      <h2 className="plan-title">{tr('title', lang)}</h2>

      <div className="plan-field">
        <span className="plan-label">{tr('from', lang)}</span>
        {location ? (
          <span>📍 {tr('myLocation', lang)} <span className="plan-muted">({location.lat.toFixed(4)}, {location.lng.toFixed(4)})</span></span>
        ) : locating ? (
          <span className="plan-muted">{tr('locating', lang)}</span>
        ) : (
          <span>
            {locationError && <span className="plan-error">{locationError} </span>}
            <button className="plan-button" onClick={requestLocation}>{tr('enableLocation', lang)}</button>
          </span>
        )}
      </div>

      <form className="plan-field plan-search" onSubmit={runSearch}>
        <span className="plan-label">{tr('to', lang)}</span>
        <input
          className="plan-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={tr('placeholder', lang)}
          aria-label={tr('to', lang)}
        />
        <button className="plan-button" type="submit" disabled={searching}>
          {searching ? tr('searching', lang) : tr('search', lang)}
        </button>
      </form>

      {searchError && <div className="plan-error">{searchError}</div>}

      {places && !destination && (
        <div className="plan-places">
          {places.length === 0 && <div className="plan-muted">{tr('noPlaces', lang)}</div>}
          {places.map((place, i) => (
            <button key={i} className="plan-place" onClick={() => setDestination(place)}>
              {place.name}
            </button>
          ))}
        </div>
      )}

      {destination && (
        <div className="plan-destination">
          🎯 <strong>{destination.name.split(',').slice(0, 3).join(',')}</strong>
          <button className="plan-link-button" onClick={() => setDestination(null)}>✕</button>
        </div>
      )}

      {destination && location && (
        <div className="plan-results">
          <h3 className="plan-subtitle">{tr('options', lang)}</h3>
          {options.length === 0
            ? <div className="plan-muted">{tr('noOptions', lang)}</div>
            : options.map(option => <TripOptionCard key={option.routeCode} option={option} lang={lang} />)}
        </div>
      )}
    </div>
  )
}
